/**
 * PracticeStage — Drag-and-drop guided apparatus setup with rule-based mistake detection.
 * Currently fully implemented for Pendulum (phy-9-pendulum). Other experiments show a placeholder.
 */
import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, AlertCircle, RotateCcw, ArrowRight, Hand, Timer, Sparkles } from "lucide-react";
import { OhmPractice, ParallelogramPractice, DNAPractice, MitosisPractice } from "./FlagshipPractice";
import { BoylePractice, PrismPractice, PhotoPractice, OsmosisPractice, ChromatoPractice } from "./FlagshipPracticeV2";
import { ReflectionPractice, FrictionPractice, SpringPractice, EnzymePractice, StomataPractice } from "./FlagshipPracticeV3";
import { LeverPractice, DensityPractice, MeterBridgePractice, BeetrootPractice, GerminationPractice } from "./FlagshipPracticeV4";
import SimulationCanvas from "@/components/SimulationCanvas";

// ============================================================================
// Pendulum interactive stage
// ============================================================================
const PendulumPractice = ({ steps, onComplete }) => {
  // Interactive state
  const [stepIdx, setStepIdx] = useState(0);
  const [angleDeg, setAngleDeg] = useState(0);
  const [lengthMeasured, setLengthMeasured] = useState(false);
  const [scalePos, setScalePos] = useState({ x: 400, y: 200 });
  const scalePosRef = useRef({ x: 400, y: 200 });
  const angleRef = useRef(0);
  const [dragging, setDragging] = useState(null);
  const [swinging, setSwinging] = useState(false);
  const [timingComplete, setTimingComplete] = useState(false);
  const [swingStart, setSwingStart] = useState(null);
  const [oscillations, setOscillations] = useState(0);
  const [timerSec, setTimerSec] = useState(0);
  const [feedback, setFeedback] = useState(null); // {type: ok|warn|err, msg}
  const [stepStatus, setStepStatus] = useState({}); // { p1: ok, p2: err, ... }
  const canvasRef = useRef(null);
  const timerRef = useRef(null);

  const currentStep = steps[stepIdx];

  // Constants for the pendulum canvas
  const pivotX = 400, pivotY = 60;
  const stringLen = 260;

  const bobX = pivotX + stringLen * Math.sin((angleDeg * Math.PI) / 180);
  const bobY = pivotY + stringLen * Math.cos((angleDeg * Math.PI) / 180);

  // ---- Drag handling ----
  const onPointerDown = (tool) => (e) => {
    if (swinging) return;
    setDragging(tool);
    e.preventDefault();
  };
  const onPointerMove = (e) => {
    if (!dragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (dragging === "scale") {
      scalePosRef.current = { x, y };
      setScalePos({ x, y });
    } else if (dragging === "bob") {
      // Compute angle from pivot to pointer
      const dx = x - pivotX;
      const dy = y - pivotY;
      const rad = Math.atan2(dx, dy);
      const deg = Math.max(-45, Math.min(45, (rad * 180) / Math.PI));
      angleRef.current = deg;
      setAngleDeg(deg);
    }
  };
  const onPointerUp = () => {
    if (dragging === "scale") {
      const pos = scalePosRef.current;
      const nearString = Math.abs(pos.x - pivotX) < 60 && pos.y > pivotY && pos.y < pivotY + stringLen + 40;
      if (currentStep?.id === "p1") {
        if (nearString) {
          setLengthMeasured(true);
          setFeedback({ type: "ok", msg: currentStep.correct_hint });
          setStepStatus((s) => ({ ...s, p1: "ok" }));
        } else {
          setFeedback({ type: "warn", msg: currentStep.mistakes?.[0]?.hint || "Try again." });
          setStepStatus((s) => ({ ...s, p1: "warn" }));
        }
      }
    } else if (dragging === "bob") {
      if (currentStep?.id === "p2") {
        const absAng = Math.abs(angleRef.current);
        const hit = currentStep.mistakes?.find((m) => {
          if (m.if === "angle > 15") return absAng > 15;
          if (m.if === "angle < 3") return absAng < 3;
          return false;
        });
        if (hit) {
          setFeedback({ type: "warn", msg: hit.hint });
          setStepStatus((s) => ({ ...s, p2: "warn" }));
        } else {
          setFeedback({ type: "ok", msg: currentStep.correct_hint });
          setStepStatus((s) => ({ ...s, p2: "ok" }));
        }
      }
    }
    setDragging(null);
  };

  // ---- Release button (step p3) ----
  const releaseBob = () => {
    if (currentStep?.id !== "p3") {
      setFeedback({ type: "err", msg: "Complete the previous step first." });
      return;
    }
    if (Math.abs(angleDeg) < 3) {
      setFeedback({ type: "warn", msg: "Set a release angle first (drag the bob)." });
      return;
    }
    setSwinging(true);
    setTimingComplete(false);
    setSwingStart(Date.now());
    setOscillations(0);
    setTimerSec(0);
    // Simulate 20 oscillations
    const T = 2 * Math.PI * Math.sqrt((100 / 100) / 9.8); // ~2.006s per swing (assume L=1m)
    const startTime = Date.now();
    // Simulate at true 3x speed. Report the REAL time = elapsed * 3 for the observation.
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      const displayedTime = elapsed * 3; // what a real stopwatch would read
      setTimerSec(displayedTime);
      const currentOsc = Math.min(20, Math.floor(displayedTime / T));
      setOscillations(currentOsc);
      if (currentOsc >= 20) {
        clearInterval(timerRef.current);
        setSwinging(false);
        setTimingComplete(true); // KEEP the reading visible so student can note it
        setFeedback({ type: "ok", msg: `${currentStep.correct_hint} Note the reading: t = ${displayedTime.toFixed(2)} s for 20 oscillations.` });
        setStepStatus((s) => ({ ...s, p3: "ok" }));
      }
    }, 50);
  };

  const resetStep = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setAngleDeg(0);
    setLengthMeasured(false);
    setScalePos({ x: 400, y: 200 });
    scalePosRef.current = { x: 400, y: 200 };
    angleRef.current = 0;
    setSwinging(false);
    setTimingComplete(false);
    setOscillations(0);
    setTimerSec(0);
    setFeedback(null);
  };

  const advanceStep = () => {
    // Clean up any lingering timer/audio state before moving to next step
    if (timerRef.current) clearInterval(timerRef.current);
    setTimingComplete(false);
    if (stepIdx < steps.length - 1) {
      setStepIdx(stepIdx + 1);
      setFeedback(null);
    } else {
      // Confirm repeat for p4 and mark complete
      setStepStatus((s) => ({ ...s, p4: "ok" }));
      onComplete?.();
    }
  };

  // Cleanup timer on unmount to avoid leaks
  React.useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // Bob swing animation while swinging
  const [swingPhase, setSwingPhase] = useState(0);
  React.useEffect(() => {
    if (!swinging) return;
    const start = Date.now();
    const raf = () => {
      const t = (Date.now() - start) / 1000;
      const omega = 3 * Math.sqrt(9.8 / 1.0); // sped up 3x
      setSwingPhase(Math.cos(omega * t));
      if (swinging) requestAnimationFrame(raf);
    };
    raf();
  }, [swinging]);
  const displayAngle = swinging ? angleDeg * swingPhase : angleDeg;
  const displayBobX = pivotX + stringLen * Math.sin((displayAngle * Math.PI) / 180);
  const displayBobY = pivotY + stringLen * Math.cos((displayAngle * Math.PI) / 180);

  return (
    <div className="h-full w-full flex flex-col lg:flex-row gap-4 min-h-0">
      {/* Canvas */}
      <div className="w-full lg:flex-1 min-h-[300px] sm:min-h-[420px] lg:min-h-[500px] rounded-2xl bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-800 overflow-hidden relative">
        <svg
          ref={canvasRef}
          viewBox="0 0 800 500"
          className="w-full h-full select-none touch-none"
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          data-testid="practice-canvas"
        >
          {/* Grid */}
          {[...Array(16)].map((_, i) => (
            <line key={`gv-${i}`} x1={i * 50} y1="0" x2={i * 50} y2="500" stroke="#1e293b" strokeWidth="0.5" />
          ))}
          {[...Array(10)].map((_, i) => (
            <line key={`gh-${i}`} x1="0" y1={i * 50} x2="800" y2={i * 50} stroke="#1e293b" strokeWidth="0.5" />
          ))}

          {/* Stand */}
          <rect x="50" y="40" width="150" height="8" fill="#475569" />
          <rect x="60" y="48" width="10" height="410" fill="#64748b" />
          <rect x="20" y="458" width="240" height="14" fill="#334155" />
          {/* Horizontal rod */}
          <line x1="70" y1="60" x2={pivotX} y2={pivotY} stroke="#64748b" strokeWidth="6" />
          {/* Pivot */}
          <circle cx={pivotX} cy={pivotY} r="8" fill="#f59e0b" />

          {/* String */}
          <line x1={pivotX} y1={pivotY} x2={displayBobX} y2={displayBobY} stroke="#e5e7eb" strokeWidth="2" />
          {/* Angle arc */}
          {!swinging && Math.abs(angleDeg) > 1 && (
            <path
              d={`M ${pivotX} ${pivotY + 40} A 40 40 0 0 ${angleDeg > 0 ? 1 : 0} ${pivotX + 40 * Math.sin(angleDeg * Math.PI / 180)} ${pivotY + 40 * Math.cos(angleDeg * Math.PI / 180)}`}
              fill="none"
              stroke={Math.abs(angleDeg) > 15 ? "#ef4444" : "#22d3ee"}
              strokeWidth="2"
              strokeDasharray="4 2"
            />
          )}
          {!swinging && Math.abs(angleDeg) > 1 && (
            <text x={pivotX + 15} y={pivotY + 65} fill={Math.abs(angleDeg) > 15 ? "#ef4444" : "#22d3ee"} fontSize="14" fontFamily="monospace">
              {Math.abs(angleDeg).toFixed(0)}°
            </text>
          )}

          {/* Bob */}
          <g
            onPointerDown={onPointerDown("bob")}
            style={{ cursor: swinging ? "default" : "grab" }}
            data-testid="practice-bob"
          >
            <circle cx={displayBobX} cy={displayBobY} r="22" fill="#22d3ee" stroke="#0891b2" strokeWidth="2" />
            <circle cx={displayBobX - 6} cy={displayBobY - 6} r="5" fill="#67e8f9" opacity="0.7" />
          </g>

          {/* Meter scale (draggable) — 300px tall spanning full string length */}
          {currentStep?.id === "p1" && !lengthMeasured && (
            <g
              onPointerDown={onPointerDown("scale")}
              style={{ cursor: "grab" }}
              data-testid="practice-scale"
              transform={`translate(${scalePos.x - 18}, ${scalePos.y - 150})`}
            >
              <rect x="0" y="0" width="36" height="300" fill="#fde047" stroke="#eab308" strokeWidth="2" rx="3" />
              {[...Array(31)].map((_, i) => (
                <line key={i} x1="0" y1={i * 10} x2={i % 5 === 0 ? 36 : 18} y2={i * 10} stroke="#78350f" strokeWidth="1" />
              ))}
              {[...Array(31)].map((_, i) => (
                i % 5 === 0 ? (
                  <text key={`n-${i}`} x="40" y={i * 10 + 3} fill="#78350f" fontSize="8" fontFamily="monospace">
                    {i * 10 * 0.5}
                  </text>
                ) : null
              ))}
              <text x="-30" y="15" fill="#eab308" fontSize="11" fontFamily="monospace" fontWeight="bold">← Drag onto string</text>
            </g>
          )}
          {lengthMeasured && (
            <text x={pivotX + 20} y={pivotY + stringLen / 2} fill="#22d3ee" fontSize="14" fontFamily="monospace" data-testid="length-label">
              L = 100 cm
            </text>
          )}

          {/* Oscillation counter overlay — visible during AND after timing so student can note the reading */}
          {(swinging || timingComplete) && (
            <>
              <rect x="580" y="20" width="200" height="90" rx="10" fill="#0f172a" stroke={timingComplete ? "#22c55e" : "#22d3ee"} strokeWidth="1.5" opacity="0.95" />
              <text x="600" y="45" fill="#22d3ee" fontSize="12" fontFamily="monospace">Oscillations</text>
              <text x="600" y="75" fill="#fde047" fontSize="24" fontFamily="monospace" fontWeight="bold">{oscillations}/20</text>
              <text x="700" y="45" fill="#22d3ee" fontSize="12" fontFamily="monospace">Time (s)</text>
              <text x="700" y="75" fill="#f59e0b" fontSize="18" fontFamily="monospace">{timerSec.toFixed(2)}</text>
              {timingComplete && (
                <text x="600" y="98" fill="#22c55e" fontSize="10" fontFamily="monospace" fontWeight="bold">✓ NOTE THIS READING</text>
              )}
            </>
          )}
        </svg>
      </div>

      {/* Instruction panel */}
      <div className="w-full lg:w-96 lg:shrink-0 flex flex-col gap-3 min-h-0">
        <div className="rounded-2xl bg-slate-900/80 border border-cyan-500/30 p-5 shadow-xl">
          <div className="flex items-center gap-2 text-cyan-400 text-xs font-bold uppercase tracking-widest mb-2">
            <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 text-xs font-black">{stepIdx + 1}</div>
            Step {stepIdx + 1} of {steps.length} · Practice
          </div>
          <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs">
            <Hand className="h-3.5 w-3.5" /> Interactive
          </div>
          <p className="text-slate-100 leading-relaxed text-base" data-testid="practice-instruction">
            {currentStep?.instruction}
          </p>

          {/* Release button appears only on step p3 */}
          {currentStep?.id === "p3" && !swinging && (
            <Button
              className="mt-4 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full"
              onClick={releaseBob}
              data-testid="practice-release-btn"
            >
              <Timer className="mr-2 h-4 w-4" /> Release & count oscillations
            </Button>
          )}
        </div>

        {/* Feedback */}
        {feedback && (
          <div
            className={`rounded-2xl p-4 border ${
              feedback.type === "ok" ? "bg-emerald-900/40 border-emerald-500/50 text-emerald-200"
              : feedback.type === "warn" ? "bg-amber-900/40 border-amber-500/50 text-amber-200"
              : "bg-rose-900/40 border-rose-500/50 text-rose-200"
            }`}
            data-testid="practice-feedback"
          >
            <div className="flex gap-2">
              {feedback.type === "ok" ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />}
              <div className="text-sm leading-relaxed">{feedback.msg}</div>
            </div>
          </div>
        )}

        {/* Step tracker */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">All steps</div>
          <ul className="space-y-1.5">
            {steps.map((s, i) => (
              <li key={s.id} className={`flex items-center gap-2 text-xs ${i === stepIdx ? "text-cyan-300" : stepStatus[s.id] === "ok" ? "text-emerald-400" : "text-slate-500"}`}>
                {stepStatus[s.id] === "ok" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <div className={`w-3.5 h-3.5 rounded-full border ${i === stepIdx ? "border-cyan-400" : "border-slate-600"}`} />}
                <span className="truncate">{s.instruction}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 flex-1" onClick={resetStep} data-testid="practice-reset">
            <RotateCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          <Button
            size="sm"
            className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold rounded-full"
            onClick={advanceStep}
            disabled={!stepStatus[currentStep?.id] || stepStatus[currentStep?.id] !== "ok"}
            data-testid="practice-next"
          >
            {stepIdx < steps.length - 1 ? "Next step" : "Go to Observe"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Generic Practice — for experiments without a custom drag-and-drop scene.
// Renders the real 2D simulation plus the same sliders the free-mode workspace
// uses, and a 3-step "Explore → Compare → Reflect" guided flow.
// ============================================================================
const GenericPractice = ({ experiment, onComplete }) => {
  const [params, setParams] = useState(() => {
    const init = {};
    (experiment.params || []).forEach((p) => { init[p.key] = p.default; });
    return init;
  });
  const [stepIdx, setStepIdx] = useState(0);
  const [snapshots, setSnapshots] = useState([]);

  const steps = [
    { title: "Explore a low setting", hint: "Move the sliders to a LOW value on the main variable, then click Snapshot." },
    { title: "Compare a high setting", hint: "Now push the same variable HIGH. Snapshot again — notice how the outcome changes." },
    { title: "Reflect", hint: "Try a mid-value. Snapshot to lock it in. When you're satisfied, jump to Observe." },
  ];
  const currentStep = steps[stepIdx];

  const setParam = (key, val) => setParams((p) => ({ ...p, [key]: val }));

  const snapshot = () => {
    setSnapshots((s) => [...s, { step: stepIdx, params: { ...params } }]);
    if (stepIdx < steps.length - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      onComplete?.();
    }
  };

  const resetParams = () => {
    const init = {};
    (experiment.params || []).forEach((p) => { init[p.key] = p.default; });
    setParams(init);
  };

  const done = stepIdx === steps.length - 1 && snapshots.length >= steps.length;

  return (
    <div className="h-full w-full flex flex-col lg:flex-row gap-4 min-h-0">
      {/* Simulation canvas */}
      <div className="w-full lg:flex-1 min-h-[280px] sm:min-h-[360px] lg:min-h-[400px] rounded-2xl bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-800 overflow-hidden relative flex items-center justify-center p-2">
        <SimulationCanvas experiment={experiment} params={params} />
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-96 lg:shrink-0 flex flex-col gap-3 min-h-0">
        <div className="rounded-2xl bg-slate-900/80 border border-amber-500/30 p-4 sm:p-5 shadow-xl">
          <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-widest mb-2">
            <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-300 text-xs font-black">{stepIdx + 1}</div>
            Step {stepIdx + 1} of {steps.length} · Practice
          </div>
          <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs">
            <Sparkles className="h-3.5 w-3.5" /> Guided exploration
          </div>
          <h4 className="text-slate-100 font-outfit font-black text-lg leading-tight" data-testid="practice-instruction">{currentStep.title}</h4>
          <p className="mt-2 text-slate-300 leading-relaxed text-sm">{currentStep.hint}</p>
        </div>

        {/* Slider controls */}
        <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-4 space-y-4 overflow-y-auto max-h-[45vh]">
          <div className="text-xs uppercase tracking-widest text-cyan-300 font-bold">Try adjusting</div>
          {(experiment.params || []).map((p) => (
            <div key={p.key}>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-slate-300 text-xs">{p.label}</Label>
                {p.type !== "select" && (
                  <span className="text-cyan-300 font-mono text-xs" data-testid={`practice-value-${p.key}`}>
                    {params[p.key]} {p.unit || ""}
                  </span>
                )}
              </div>
              {p.type === "select" ? (
                <Select value={params[p.key]} onValueChange={(v) => setParam(p.key, v)}>
                  <SelectTrigger className="h-9 bg-slate-800 border-slate-700 text-slate-100 text-xs" data-testid={`practice-select-${p.key}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(p.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Slider
                  min={p.min}
                  max={p.max}
                  step={p.step}
                  value={[+params[p.key]]}
                  onValueChange={(v) => setParam(p.key, v[0])}
                  data-testid={`practice-slider-${p.key}`}
                />
              )}
            </div>
          ))}
          {(experiment.params || []).length === 0 && (
            <p className="text-slate-500 text-xs">This experiment is observational — just watch the animation and take snapshots.</p>
          )}
        </div>

        {/* Snapshot list */}
        {snapshots.length > 0 && (
          <div className="rounded-2xl bg-slate-900/60 border border-slate-800 p-3">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Snapshots</div>
            <ul className="space-y-1 text-xs text-slate-400">
              {snapshots.map((s, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 text-emerald-400" /> Snapshot {i + 1} recorded
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 flex-1" onClick={resetParams} data-testid="practice-reset">
            <RotateCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          <Button
            size="sm"
            className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full"
            onClick={snapshot}
            data-testid="practice-next"
          >
            {stepIdx < steps.length - 1 ? "Snapshot & continue" : done ? "Go to Observe" : "Snapshot & finish"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Placeholder for other flagship experiments (until iter-2 fills them in)
// ============================================================================
const PracticePlaceholder = ({ experiment, onComplete }) => (
  <div className="h-full flex items-center justify-center">
    <div className="max-w-md text-center p-8 rounded-3xl bg-slate-900/70 border border-slate-800">
      <Hand className="mx-auto h-12 w-12 text-cyan-400 mb-4" />
      <h3 className="font-outfit font-black text-2xl text-slate-100">Guided practice coming soon</h3>
      <p className="mt-3 text-slate-400 leading-relaxed">
        The drag-and-drop practice mode for <b>{experiment.title}</b> is being built.
        You&apos;ve already watched the 3D explanation — for now, jump ahead to record your observations.
      </p>
      <Button className="mt-6 bg-cyan-500 hover:bg-cyan-400 text-slate-950 rounded-full" onClick={onComplete} data-testid="practice-skip">
        Skip to Observe <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  </div>
);

// ============================================================================
// Router
// ============================================================================
export const PracticeStage = ({ experiment, steps, onComplete }) => {
  if (experiment.id === "phy-9-pendulum" && steps?.length) {
    return <PendulumPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-9-circuit-bulbs" && steps?.length) {
    return <OhmPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-11-parallelogram" && steps?.length) {
    return <ParallelogramPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "bio-12-dna" && steps?.length) {
    return <DNAPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "bio-10-mitosis" && steps?.length) {
    return <MitosisPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-11-boyle" && steps?.length) {
    return <BoylePractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-10-prism" && steps?.length) {
    return <PrismPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "bio-10-photosynthesis" && steps?.length) {
    return <PhotoPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "bio-10-osmosis" && steps?.length) {
    return <OsmosisPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "chem-11-chromato" && steps?.length) {
    return <ChromatoPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-9-reflection" && steps?.length) {
    return <ReflectionPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-11-friction" && steps?.length) {
    return <FrictionPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-11-spring" && steps?.length) {
    return <SpringPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "bio-12-enzyme" && steps?.length) {
    return <EnzymePractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "bio-12-stomata" && steps?.length) {
    return <StomataPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-10-lever" && steps?.length) {
    return <LeverPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-9-density" && steps?.length) {
    return <DensityPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "phy-12-meterbridge" && steps?.length) {
    return <MeterBridgePractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "bio-11-beetroot" && steps?.length) {
    return <BeetrootPractice steps={steps} onComplete={onComplete} />;
  }
  if (experiment.id === "bio-9-germination" && steps?.length) {
    return <GerminationPractice steps={steps} onComplete={onComplete} />;
  }
  return <GenericPractice experiment={experiment} onComplete={onComplete} />;
};

export default PracticeStage;
